import { apiGet, apiSend } from '../../session/client';

/**
 * Everything the Orders page reads and writes, and nothing it invents.
 *
 * Each call is an existing consumer route — the ones the Android app's order
 * screens use (sections 11 and 15–18). Every route is keyed on this browser's
 * device handle, which `apiFetch` sends; when somebody is signed in it also
 * sends their access token, and the platform then adds the orders that
 * account placed on any other device. There is no person id in any path or
 * body: the server decides whose orders these are.
 */

export type OrderStatus =
  | 'PLACED'
  | 'AWAITING_REVIEW'
  | 'CONFIRMED'
  | 'PREPARING'
  | 'READY_FOR_COLLECTION'
  | 'OUT_FOR_DELIVERY'
  | 'COMPLETED'
  | 'CANCELLED';

export interface OrderLine {
  readonly id: string;
  readonly listingId: string;
  readonly title: string;
  readonly quantity: number;
  readonly unitPriceMinor: number;
  readonly lineTotalMinor: number;
  readonly imageUrl?: string;
  readonly consultationId?: string;
}

export interface Substitution {
  readonly id: string;
  readonly lineId: string;
  readonly replacementTitle: string;
  readonly status: 'PROPOSED' | 'ACCEPTED' | 'DECLINED' | string;
}

export interface Order {
  readonly id: string;
  readonly reference: string;
  readonly storefrontId: string;
  readonly currency: string;
  readonly lines: readonly OrderLine[];
  readonly itemsTotalMinor: number;
  readonly deliveryFeeMinor: number;
  readonly totalMinor: number;
  readonly rewards?: { readonly redemption?: { readonly discountMinor: number } };
  readonly fulfilment: 'COLLECTION' | 'DELIVERY';
  readonly deliveryAddress?: {
    readonly line1: string;
    readonly line2?: string;
    readonly town: string;
    readonly postcode: string;
  };
  readonly status: OrderStatus;
  readonly history: readonly { readonly status: OrderStatus; readonly at: string }[];
  readonly payment: { readonly status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' };
  readonly cancellation?: {
    readonly at: string;
    readonly by: 'CUSTOMER' | 'PROVIDER' | 'SYSTEM';
    readonly reason: string;
  };
  readonly substitutions?: readonly Substitution[];
  readonly placedAt: string;
  readonly storefront?: { readonly id: string; readonly slug: string; readonly name: string };
}

export interface Tracking {
  readonly order: Order;
  readonly journey: readonly OrderStatus[];
  readonly reached: number;
  readonly finished: boolean;
}

export interface CourierView {
  readonly status?: 'ACCEPTED' | 'PICKED_UP' | 'DELIVERED' | string;
  readonly courier: { readonly displayName: string; readonly vehicle: string } | null;
  readonly pickedUpAt?: string | null;
  readonly deliveredAt?: string | null;
  readonly recipientName?: string | null;
}

export interface Refund {
  readonly id: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: 'PENDING' | 'COMPLETED' | 'FAILED' | string;
  readonly requestedAt: string;
  readonly reason?: string;
  readonly completedAt?: string;
}

export interface Refunds {
  readonly refunds: readonly Refund[];
}

export interface CourierMessage {
  readonly id: string;
  readonly from: 'CUSTOMER' | 'COURIER' | string;
  readonly body: string;
  readonly at: string;
}

export interface StorefrontPlace {
  readonly name: string;
  readonly locations: readonly { readonly town?: string; readonly postcode?: string }[];
}

/** The route answers at most 50, newest first. 50 back means "at least 50". */
export const ORDERS_LIMIT = 50;

const id = (value: string): string => encodeURIComponent(value);

export const listOrders = (): Promise<readonly Order[]> =>
  apiGet<Order[]>(`/consumer/orders?limit=${String(ORDERS_LIMIT)}`);

export const getTracking = (orderId: string): Promise<Tracking> =>
  apiGet<Tracking>(`/consumer/orders/${id(orderId)}/tracking`);

export const getCourier = (orderId: string): Promise<CourierView> =>
  apiGet<CourierView>(`/consumer/orders/${id(orderId)}/courier`);

export const getHandover = (orderId: string): Promise<{ collection: { code: string } | null }> =>
  apiGet<{ collection: { code: string } | null }>(`/consumer/orders/${id(orderId)}/handover`);

export const getRefunds = (orderId: string): Promise<Refunds> =>
  apiGet<Refunds>(`/consumer/orders/${id(orderId)}/refunds`);

export const getMessages = (orderId: string): Promise<readonly CourierMessage[]> =>
  apiGet<CourierMessage[]>(`/consumer/orders/${id(orderId)}/messages`);

export const sendMessage = (orderId: string, body: string): Promise<CourierMessage> =>
  apiSend<CourierMessage>('POST', `/consumer/orders/${id(orderId)}/messages`, { body });

/** Where the shop is, for Directions: the storefront's own published locations. */
export const getStorefrontPlace = (slug: string): Promise<StorefrontPlace> =>
  apiGet<StorefrontPlace>(`/consumer/storefronts/${id(slug)}?limit=1`);

/**
 * Cancel — designs 11.3 / 11.4. The domain decides whether it may; a refusal
 * comes back with the platform's own sentence. One idempotency key per press,
 * so a retried request cannot cancel twice.
 */
export const cancelOrder = (
  orderId: string,
  reason: string | undefined,
  idempotencyKey: string,
): Promise<Order> =>
  apiSend<Order>(
    'POST',
    `/consumer/orders/${id(orderId)}/cancel`,
    // No reason chosen: the platform records its own default.
    reason === undefined ? {} : { reason },
    { headers: { 'idempotency-key': idempotencyKey } },
  );
